import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { useBookmarkSelection } from '../../contexts/BookmarkSelectionContext';
import { useBatchActions } from '../../hooks/useBatchActions';
import { useCurrentUser } from '../../services/userHooks';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import Icon from '../ui/Icon';
import { BatchMovePopoverProps } from './types';

function BatchMovePopoverContent({
  bookmarkIds,
  closePopover,
}: BatchMovePopoverProps & { closePopover: () => void }) {
  const { batchMove } = useBatchActions();
  const { currentUser } = useCurrentUser();
  const { clearSelection } = useBookmarkSelection();

  const folders = currentUser?.folders || [];

  const { selectedIndex, setItemRef, handleItemHover, handleItemClick } =
    useKeyboardNavigation({
      items: folders,
      onSelect: (folder) => handleMove(folder.id),
      onEscape: closePopover,
      isActive: true,
    });

  const handleMove = async (folderId: string) => {
    await batchMove.mutateAsync({
      bookmarkIds,
      folderId,
    });

    clearSelection();
    closePopover();
  };

  return (
    <div
      className="max-h-[250px] w-[200px] overflow-x-hidden overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      {folders.length === 0 ? (
        <p className="menu-item disabled">
          <Trans>No folders available</Trans>
        </p>
      ) : (
        <>
          {folders.map((folder, index) => (
            <button
              key={folder.id}
              ref={setItemRef(index)}
              onClick={() => handleItemClick(index)}
              onMouseEnter={() => handleItemHover(index)}
              disabled={batchMove.isPending}
              className="menu-item min-w-0"
            >
              <div className="flex w-full items-center gap-2">
                <Icon
                  name="folder"
                  className={clsx(
                    'menu-item-icon',
                    selectedIndex === index ? 'active' : '',
                  )}
                />
                <span className="truncate whitespace-nowrap">{folder.displayTitle}</span>
                {batchMove.isPending && selectedIndex === index && (
                  <span className="text-copy-secondary flex-shrink-0 text-sm">
                    <Trans>Loading...</Trans>
                  </span>
                )}
              </div>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

function BatchMovePopover({ bookmarkIds, onClose }: BatchMovePopoverProps) {
  return (
    <BatchMovePopoverContent
      bookmarkIds={bookmarkIds}
      onClose={onClose}
      closePopover={onClose}
    />
  );
}

export default BatchMovePopover;
